import { ObjectId } from "@/model/db/General";
import { CurrencyValue } from "@/modules/abstract-ui/forms/input/BFCurrency";
import { CDNBucketObject } from "@/services/CDNService";
import { HistoryEntryObject } from "../../../components/HistoryViewer/IHistoryEntry";
import { CurrencyAmount } from "../../../model/common/CommonInterfaces";
import { Contact } from "../../../model/db/Contact";
import BaseAsset, {
  TypedBaseAsset,
} from "../../../model/general-assets/BaseAsset";
import { ExistentUploadEntry } from "../../../modules/abstract-ui/forms/upload/BFUpload";
import { DocumentStoreDocument } from "../../../modules/document-store/DSInterfaces";
import { BaseConstants } from "../../../redux/hooks";
import { MatchQuery } from "../../../services/DataService";
import {
  EntityStructure,
  InvoiceStructure,
  UnitStructure,
} from "../../AppConfigInterfaces";
import { LinkedAsset } from "../cashBudget/model/CashBudgetEntry";
import {
  InvoiceApprovalChainRules,
  InvoiceCheckChainRules,
  TypeOfBankTransfer,
} from "../invoiceConfigApp/RACInterfaces";
export type RAConstants = BaseConstants & {};

export enum EInvoiceExtractionErrorType {
  NONE,
  NO_PDF,
  CANT_DO_ANYTHING,
}

export class InvoiceExtraction {
  invoiceDate?: string | Date; // Datum im Format JJJJ-MM-TT (ISO), sonst null
  paymentAmountBase?: number; // Nettobetrag (ohne Währungssymbole, z.B. 1000.50)
  paymentAmountTax?: number; // Steuerbetrag (ohne Symbole, z.B. 190.00)
  paymentAmountTotal?: number; // Bruttobetrag (ohne Symbole, z.B. 1190.50)
  invoiceNumber?: string; // Rechnungsnummer (z.B. "R-2024-001")
  customerNumber?: string; // Kundennummer (z.B. "KUNDE-123")
  paymentReference?: string; // Zahlungsreferenz (z.B. "ZAHL-REF-456")
  bankIban?: string; // IBAN (ohne Leerzeichen, z.B. "DE9110000000123456789")
  bankSwift?: string; // SWIFT/BIC (z.B. "DEUTDEFF")
  supplierCompanyName?: string; // Lieferant (z.B. "TechGadgets GmbH")
  supplierAddress?: {
    street: string; // Straße (z.B. "Musterstr. 1")
    postalCode: string; // PLZ (z.B. "12345")
    city: string; // Stadt (z.B. "Berlin")
    country: string; // Land (z.B. "Deutschland")
  };
  supplierPhoneNumber?: string; // Telefon (z.B. "+49 30 1234567")
  supplierEmail?: string; // E-Mail (z.B. "info@techgadgets.de")
  supplierWebsite?: string; // Website (z.B. "https://techgadgets.de")
  customerContactName?: string; // Kundenname (z.B. "Max Mustermann")
  customerCompanyName?: string; // Kunden Firmenname (z.B. "Acme Corp")
  customerAddress?: {
    street: string; // Straße (z.B. "Musterstr. 1")
    postalCode: string; // PLZ (z.B. "12345")
    city: string; // Stadt (z.B. "Berlin")
    country: string; // Land (z.B. "Deutschland")
  };
  currencyCode?: string; // Währungscode (ISO 4217, z.B. "EUR")
}

export interface ExtractionAsset extends BaseAsset {
  extract?: InvoiceExtraction;
}

export enum InvoiceExtractionStatus {
  init = "init",
  abort = "abort",
  loading = "loading",
  finish = "finish",
  fail = "fail",
}

/**
 * translations for kinds
 * i18n.t("Invoice.kinds.INVOICE", "Eingangsrechnung");
 * i18n.t("Invoice.kinds.STORNO", "Storno-Rechnung");
 * i18n.t("Invoice.kinds.PREPAYMENT", "Pro-Forma-Rechnung");
 * i18n.t("Invoice.kinds.CREDITCARD_BILL", "Kreditkartenabrechnung");
 * i18n.t("Invoice.kinds.REIMBURSEMENT_OF_EXPENSES", "Auslagenerstattung");
 * i18n.t("Invoice.kinds.INTERNAL_PAYMENT_ORDER", "Interne Zahlungsauftrag");
 * i18n.t("Invoice.kinds.REMINDER", "Mahnung");
 *
 */

export enum InvoiceKind {
  "INVOICE" = "INVOICE",
  "STORNO" = "STORNO",
  // "PREPAYMENT" = "PREPAYMENT",
  // "CREDITCARD_BILL" = "CREDITCARD_BILL",
  // "REIMBURSEMENT_OF_EXPENSES" = "REIMBURSEMENT_OF_EXPENSES",
  // "INTERNAL_PAYMENT_ORDER" = "INTERNAL_PAYMENT_ORDER",
  "DUNNING" = "REMINDER",
}

type PaymentTypeDataBankTransfer = {};

type PaymentTypeData = PaymentTypeDataBankTransfer;

export enum InvoiceTask {
  NONE = "NONE",
  APPROVAL = "APPROVAL",
  BOOKING = "BOOKING",
  CHECKING = "CHECKING",
  PAYING = "PAYING",
}

type InvoiceBaseDataInvoiceData = {
  EInvoiceErrorType?: EInvoiceExtractionErrorType;
  extractStatus: InvoiceExtractionStatus;
  invoiceType: ObjectId;
  file: ExistentUploadEntry;
  fileImages: ObjectId[];
  XMLfile?: ExistentUploadEntry;
  value: CurrencyAmount;
  documentDate?: Date;
  contact?: ObjectId;
  invoiceId?: string;

  custom?: {
    identifier: string;
    value: string;
  }[];
};
type InvoiceBaseData = {
  kind: InvoiceKind;
  direction: InvoiceDirection;

  entity: ObjectId;
  objectId: ObjectId;

  type: string;

  reminder?: {
    reminderLevel: number;
    reminderData: {
      reminderId: ObjectId;
      date: Date;
      status: "active" | "archived";
      reminderLevel: number;
      statusChangeDate: Date;
      statusChangeUser: ObjectId;
      statusChangeComment: string;
      value: number;
    }[];
  };
  status: InvoiceStatus;
  currentTask: InvoiceTask[];

  //booking
  // needsToBeBooked: boolean;
  booked: boolean;
  bookingDate: Date;

  tags: string[];

  installmentSum: number;
  completeSum: number;
  // needsToBeChecked: boolean;

  payment: PaymentEntry[];
  urgent?: UrgentData;
  activity?: LinkedAsset[];
  history: HistoryEntryObject[];
  // needsToBePaid: boolean;
  paid: boolean;
  paymentDate: Date;

  invoice: InvoiceBaseDataInvoiceData & {
    paymentType: ObjectId; // ZM
    paymentTypeData: PaymentData; // ZM Info
  };
  checkRevertBooking?: boolean;

  approval: {
    approvalChain: InvoiceApprovalChainRules[];
  };
  installment: PaymentEntry[];

  costCenter?: RAInvoiceCostCenter[];

  attachments?: DocumentStoreDocument[];

  reminderFor?: ObjectId;
  reminderLevel?: number;

  changeRequestId?: ObjectId;

  correction: {
    isCorrection: boolean;
    isStorno: boolean;
    linkedInvoice: ObjectId;
    correctedInvoiceId: ObjectId[];
    stornoId: ObjectId[];
  };
  deferred?: {
    date: Date;
    user: ObjectId;
    comment: string;
  };
  opos?: InvoiceOpos;
};

export type InvoiceOpos = {
  value: number;
  booked: number;
  oposCalculate: boolean;
  sumComplete: number;
  openSum: number;
  bookedInfo: InvoiceOposBookedInfo[];
  manuallyInfo: InvoiceOposManuallyInfo[];
  sumComplete7Days: number;
  openSum7Days: number;
  completeOpen: number;
};
export type InvoiceOposManuallyInfo = {
  comment: string;
  date: Date;
  id: ObjectId;
  user: ObjectId;
  value: number;
};
export type InvoiceOposBookedInfo = {
  bookingId: ObjectId;
  value: number;
  date: Date;
  user: ObjectId;
};

export type PaymentEntry = {
  id: ObjectId;
  isChecked: boolean;
  checkedDate: Date;
  checkedUser: ObjectId;

  usage: string;
  amount: number;
  date: Date; // the date when the payment should be payed by

  payedDate: Date | null;
  payedLinkUser: ObjectId | null; // the user who payed the payment

  executedDate: Date | null;
  linkedPaymentObject: ObjectId | null;
  createPaymentObject: boolean;
  payedLinkBooking?: ObjectId;
};
export type SkontoFields = {
  daysForPayment: number;
  discountPercentage: number;
};
export type SkontoPaymentEntry = PaymentEntry & {
  discounts?: SkontoFields[];
};
export type PaymentFormEntry = {
  usage: string;
  amount: number;
  date: Date;
  createPaymentObject?: boolean;
  payedDate?: Date | null;
};
export type SkontoPaymentFormEntry = PaymentFormEntry & {
  discounts?: SkontoFields[];
};

export type InvoiceRootData = {
  intern?: {
    invoiceTaskId: string;
    plannedBookings: string[];
  };
  _deleted?: boolean;
  _declined?: boolean;
  _booked?: boolean;
};

export type InvoiceBookingData = {
  kind: InvoiceDirection;
  file: CDNBucketObject;
  value: {
    currency: string;
    amount: number;
  };
  reverted?: boolean;
  date: Date;
  serviceDate?: Date; // Leistungsdatum
  creationDate: Date;
  account: ObjectId; // contactId
  contraAccount: ObjectId;
  BU: ObjectId;

  referenceField: string;
  usage: string;
  cost1: string;
  cost2: string;
  extraInfo: string[];

  entity: ObjectId;
  type: string;
  objectId: ObjectId;
  linkedAsset: LinkedAsset;

  export?: Date;
};
export type InvoiceBooking = TypedBaseAsset<InvoiceBookingData> & {
  expand: {
    account: Contact;
    contraAccount: InvoiceAccount;
    BU: InvoiceAccountBU;
  };
};

export type Invoice = TypedBaseAsset<InvoiceBaseData> &
  ExtractionAsset &
  InvoiceRootData;

// #####################################################
// ############### OLD STUFF HERE ######################
// #####################################################

export type AppConfigInvoice = {
  entities: EntityStructure;
  invoiceTypes: InvoiceStructure;
  businessUnits: string[];
  businessUnitConfig: UnitStructure;
};
// approval chain elements - not really necessary right now in FE

export interface InvoiceApprovalPrefilterElements {
  type: "op";
  name: "objectId" | "entity";
  op: "in";
  value: string[];
}

export interface PrefilterStruct {
  filter: InvoiceApprovalPrefilterElements;
  chainElements: InvoiceApprovalChainElements[];
}

export type InvoiceApprovalChainVersionData = {
  rules: InvoiceApprovalChainVersionDataRules[];
};

export interface InvoiceApprovalChainVersionDataRules {
  valueBoundary: number;
  switchFilter: {
    matchQuery: MatchQuery;
    value: {
      users: string[];
      teams: string[];
    };
  }[];
  default: {
    users: string[];
    teams: string[];
  };
}

export interface InvoiceApprovalChain {
  _id: string;
  data: {
    version?: number;
    versionData?: InvoiceApprovalChainVersionData;
    type: string;
    displayName: string;
    status: "active";
    prefilter: PrefilterStruct[];
    chainElements: InvoiceApprovalChainElements[];
  };
}

export type InvoiceApprovalChainElements = InvoiceApprovalChainVersionDataRules;

// end of chain elements

export class PaymentPlan {
  amount: number;
  date: Date;
  payedDate: Date | null;
  linkedPaymentObject: string | null;
  extra: boolean;
  activeDate?: Date;
  usage?: string;
}

export type ExtractValue<T> = {
  value: T;
  extractValue: T;
  box?: {
    top: number;
    left: number;
    width: number;
    height: number;
    page: number;
  };
};

export type InvoiceCustomField = {
  label: string;
  value: string;
};
export type ExtractStatus = "init" | "loading" | "finish" | "abort" | "fail";
export type InvoiceStatusEnd = "approved" | "declined";
export type InvoiceStatus = InvoiceStatusEnd | "init" | "inApproval";

export type RALinkedAsset = {
  assetId: string;
  assetType: string;
};
export type RAInvoiceCostCenter = {
  costCenterId: string;
  objectId?: string;
  value: number;
  info: string;
};

export type PaymentIBAN = {
  type: TypeOfBankTransfer.PAYMENT_IBAN;
  iban: string;
};
export type PaymentIBANInternational = {
  type: TypeOfBankTransfer.PAYMENT_IBAN_INTERNATIONAL;
  iban: string;
  bic: string;
};
export type PaymentUSNational = {
  type: TypeOfBankTransfer.PAYMENT_INTERNATIONAL;
  account: string;
  routing?: string;
  swift?: string;
  nameOfBank?: string;
  addressOfBank?: string;
};
export type PaymentNONE = {
  type: "NONE";
};

export type PaymentDataWithUsageAndDueDate = {
  usage: string;
  dueDate: Date;
} & PaymentData;

export type PaymentData =
  | PaymentNONE
  | PaymentIBAN
  | PaymentUSNational
  | PaymentIBANInternational;

export type InvoiceDirection = "INCOMING" | "OUTGOING";

export type UrgentData = {
  isUrgent: boolean;
  urgentComment?: string;
  date?: Date;
  user?: string;
};

export interface BudgetBalanceStatistic {
  _id: string;
  balance: number;
  lastBooking: Date;
}

export enum InvoiceAccountDataCategory {
  "NONE" = "NONE",
  "GENERAL_LEDGER" = "GENERAL_LEDGER",
  "DEBTOR" = "DEBTOR",
  "CREDITOR" = "CREDITOR",
}

export type InvoiceAccountData = {
  entity: ObjectId;
  accountNumber: string;
  defaultBU?: ObjectId;
  description: string;
  detailedDescription: string;
  category: InvoiceAccountDataCategory;
  status: "active" | "archived";
};
export type InvoiceAccount = TypedBaseAsset<InvoiceAccountData>;
export type InvoiceAccountBUData = {
  entity: ObjectId;
  accountNumber: string;
  description: string;
  status: "active" | "archived";
  ust: number;
};
export type InvoiceAccountBU = TypedBaseAsset<InvoiceAccountBUData>;

export type InvoiceBookingEntryForm = {
  BU: ObjectId;
  accountNumber: ObjectId;
  referenceField: string;
  date: Date;
  serviceDate?: Date; // Leistungsdatum
  usage: string;
  cost1: string;
  cost2: string;
  extraInfo: string[];
  value: number;

  BUCode?: string;
  accountNumberCode?: string;
  amount?: number;
  currency?: string;
};
export type InvoiceInstallmentForm = {
  amount: number;
  date: Date;
  referenceId: string;
  entries: InvoiceBookingEntryForm[];
};
export type BookInvoiceFormData = {
  contactAccount: string;
  paymentPlan: InvoiceBookingEntryForm[];
  installment?: InvoiceInstallmentForm[];
};

export type InvoicePaymentData = {
  value: CurrencyValue;
  startDate: Date;
  // dueDate: Date
  documentDate: Date;
  entity: ObjectId;
  objectId: ObjectId;
  bankAccount: ObjectId;
  metaData?: any;
  // usage: string
  contactId: ObjectId;
  type: string;
  recipient: string;
  paymentTypeData: PaymentDataWithUsageAndDueDate;
  ignoreInLiquidityPlanning: boolean; // if a payment should get out of the liquidityplanning
  // recipient: {
  //     type: "EUR" | "USD" | "DE"
  //     info: { // for DE recipient
  //         name: string
  //         iban: string
  //         bic?: string
  //     }
  // }
  checked: boolean;
  checkedInfo: {
    approvalChain: InvoiceCheckChainRules[];
    date: Date;
  };
  checkedHistory?: {
    userId: ObjectId;
    date: Date;
  }[];
  payed: boolean;
  payedInfo?: {
    date: Date;
    user: ObjectId;
  };
  linkedAsset: {
    type: string; // invoice
    id: ObjectId;
  };
  tags: string[];
};

export type InvoicePayment = TypedBaseAsset<InvoicePaymentData>;
export type InvoicePaymentWithInvoice = TypedBaseAsset<
  InvoicePaymentData & {
    metaInfo: {
      numComments: number;
      urgent: UrgentData;
      invoiceType: ObjectId;
      invoiceKind: InvoiceKind;
    };
  }
>;

export type InvoicePaymentWithInvoiceWithData = InvoicePaymentWithInvoice & {
  past: {
    userId: ObjectId;
    date: Date;
  }[];
  next: string[] | null;
  allowedToRelease: string[];
};

export type InvoicePaymentWithInvoiceWithCalculations =
  InvoicePaymentWithInvoice & {
    paymentAmount: number;
    reminder: null | {
      amount: number;
      checkedReminders: ObjectId[];
      entries: PaymentDunning[];
    };
    credit: null | {
      amount: number;
      entries: {
        id: ObjectId;
        amount: number;
        open: number;
        total: number;
        paymentEntry: InvoicePaymentWithInvoice;
      }[];
      otherCreditsAvailable: InvoicePaymentWithInvoice[];
    };
    otherCredit: null | InvoicePaymentWithInvoice[];
  };

export type PaymentDunning = {
  invoiceId: ObjectId;
  reminderId: ObjectId;
  status: "active" | "payed" | "aborted";
  date: Date;
  statusChangeDate: Date;
  statusChangeUser: ObjectId;
  statusChangeComment: string;

  value: number;
  reminderLevel: number;
};

export type PaymentResponse = {
  payments: InvoicePaymentWithInvoice[];
  positivePayments?: InvoicePaymentWithInvoice[];
  reminders: PaymentDunning[];
};

export type InvoiceExportHistoryData = {
  file: CDNBucketObject;
  entity: ObjectId[];
  direction: InvoiceDirection;
  type: string[];
};
export type InvoiceExportHistory = TypedBaseAsset<InvoiceExportHistoryData>;

export type InvoiceAccountingRuleData = {
  entity: ObjectId;
  direction: "INCOMING" | "OUTGOING";
  contactId: ObjectId;
  accounts: ObjectId[];
};
export type InvoiceAccountingRule =
  TypedBaseAsset<InvoiceAccountingRuleData> & {
    expand: {
      accounts: InvoiceAccount[];
    };
  };

export enum InvoiceChangeRequestStatus {
  open = "open",
  declined = "declined",
  approved = "approved",
}
export type InvoiceChangeRequestData = {
  request: any;
  invoiceId: ObjectId;
  direction: InvoiceDirection;
  comment: string;
  type: string;
  status: InvoiceChangeRequestStatus;
};
export type InvoiceChangeRequest = TypedBaseAsset<InvoiceChangeRequestData> & {
  expand: {
    invoiceId: Invoice;
  };
};
// export type InvoicePaymentData = {
//   value: CurrencyAmount;
//   startDate: Date;
//   documentDate: Date;
//   entity: ObjectId;
//   objectId: ObjectId;
//   bankAccount: ObjectId;
//   type: string;
//   recipient: string;
//   contactId: ObjectId;
//   paymentTypeData: PaymentData;
//   ignoreInLiquidityPlanning: boolean;
//   checked: boolean;
//   checkedInfo: {
//     approvalChain: [];
//     date: {
//       $date: "2025-05-14T12:51:10.744Z";
//     };
//   };
//   payed: boolean;
//   linkedAsset: LinkedAsset;
//   tags: [];
//   metaInfo: {
//     numComments: number;
//     invoiceType: ObjectId;
//     reminder: {
//       reminderLevel: 1;
//       reminderData: [
//         {
//           reminderId: {
//             $oid: "68248826f0df88c42c21e722";
//           };
//           date: {
//             $date: "2025-05-14T12:11:45.444Z";
//           };
//           status: "active";
//           reminderLevel: 1;
//           statusChangeDate: null;
//           statusChangeComment: null;
//           statusChangeUser: null;
//           value: 5;
//         },
//         {
//           reminderId: {
//             $oid: "68271894872daf59ef6aaf1d";
//           };
//           date: {
//             $date: "2025-05-16T23:10:42.033Z";
//           };
//           status: "active";
//           reminderLevel: 1;
//           statusChangeDate: null;
//           statusChangeComment: null;
//           statusChangeUser: null;
//           value: 5;
//         }
//       ];
//     };
//     invoiceKind: InvoiceKind;
//   };
//   payedValue: CurrencyAmount;
//   payedInfo: {
//     date: Date;
//     user: ObjectId;
//   };
// };

export type ManualBookingData = {
  amount: number;
  comment: string;
  withBooking: boolean;
  booking?: {
    accountNumber: ObjectId;
    BU: ObjectId;
    date: Date;
    serviceDate?: Date;
    usage: string;
    cost1: string;
    cost2: string;
    referenceField: string;
    extraInfo: string[];
  };
};
